import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Observable } from 'rxjs';
import { RepairTaskContractorOptionDto, RepairTaskService } from '@artemis-software/wr-api';

export interface RepairTaskCreateDialogResult {
  contractor: RepairTaskContractorOptionDto;
  title: string;
  description: string;
}

@Component({
  standalone: false,
  selector: 'wr-repair-task-create-dialog',
  templateUrl: './repair-task-create-dialog.component.html',
  styleUrls: ['./repair-task-create-dialog.component.scss'],
})
export class RepairTaskCreateDialogComponent implements OnInit {
  private readonly repairTaskService = inject(RepairTaskService);

  @Input()
  buildingId = '';

  @Input()
  initialTitle = '';

  @Input()
  initialDescription = '';

  @Input()
  contractorFilter: (option: RepairTaskContractorOptionDto) => boolean = () => true;

  @Input()
  contractorOptionsLoader?: () => Observable<RepairTaskContractorOptionDto[]>;

  @Input()
  emptyContractorMessage = 'Dem Gebäude ist kein passender Dienstleister zugewiesen.';

  readonly options = signal<RepairTaskContractorOptionDto[]>([]);
  readonly loading = signal(true);

  readonly form = new FormGroup({
    contractorId: new FormControl<string | null>(null, Validators.required),
    title: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    description: new FormControl('', { nonNullable: true }),
  });

  async ngOnInit(): Promise<void> {
    this.form.patchValue({ title: this.initialTitle, description: this.initialDescription });
    await this.loadOptions();
  }

  submitResponse = (): RepairTaskCreateDialogResult | false => {
    const contractor = this.options().find(it => it.id === this.form.controls.contractorId.value);
    if (this.form.invalid || !contractor) {
      return false;
    }
    return {
      contractor,
      title: this.form.controls.title.value.trim(),
      description: this.form.controls.description.value.trim(),
    };
  };

  private async loadOptions(): Promise<void> {
    this.loading.set(true);
    try {
      const options$ = this.contractorOptionsLoader?.() ?? this.repairTaskService.findContractorOptions(this.buildingId);
      const options = await firstValueFrom(options$);
      this.options.set(options.filter(this.contractorFilter));
    } finally {
      this.loading.set(false);
    }
  }
}
